package screens.wallColourer

import common.EConstants
import common.ECssClasses
import common.EEnvironmentVariables
import components.ButtonContent
import components.Toolbar
import components.burgerMenu.BurgerMenu
import components.colourChip.ColourChip
import components.items.ProductlistIcon
import components.items.WatchlistIcon
import components.loadingSpinner.LoadingSpinner
import components.toast.BottomToast
import components.toast.ToastItem
import core.AppContext
import core.enums.EImages
import core.enums.EJSBridgeActions
import core.enums.ELocalizationKeys
import core.enums.EPostMessageActions
import core.extensions.getValue
import core.extensions.isInNativeApp
import core.extensions.postMessageToNativeApp
import core.extensions.toClassName
import core.io.dto.ColourCollectionDTO
import core.io.dto.ColourDTO
import core.io.dto.JSBridgeActionDTO
import core.io.dto.toColourListObject
import core.ionic.IonButton
import core.ionic.IonCol
import core.ionic.IonContent
import core.ionic.IonGrid
import core.ionic.IonHeader
import core.ionic.IonPage
import core.ionic.IonRow
import core.localStorage.LocalStorageDAO
import core.localization.Localization
import core.piwik.Piwik
import core.util.exhaustive
import csstype.AlignItems
import csstype.Display
import csstype.JustifyContent
import csstype.LineStyle
import csstype.NamedColor
import csstype.Position
import csstype.number
import csstype.pct
import csstype.plus
import csstype.px
import emotion.react.css
import io.ktor.http.*
import kotlinext.js.asJsObject
import kotlinx.browser.window
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import org.w3c.dom.HTMLIFrameElement
import org.w3c.dom.MessageEvent
import org.w3c.dom.events.Event
import react.FC
import react.Props
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.iframe
import react.dom.html.ReactHTML.img
import react.router.Navigate
import react.router.useNavigate
import react.router.useParams
import react.useContext
import react.useEffect
import react.useEffectOnce
import react.useMemo
import react.useRef
import react.useState
import screens.EScreens
import screens.colourFinder.ResizeObserver

/**
 * Copyright (c) emgress GmbH. All rights reserved.
 *
 * Created on : 16.11.22
 * Author : leonhergt
 */

external interface ShowSelectedColoursOnWallScreenProps : Props {
}

val ShowSelectedColoursOnWallScreen = FC<ShowSelectedColoursOnWallScreenProps> { props ->

    val mContext by useContext(AppContext)
    val (mLocalStorage, mLocalStorageDispatch) = mContext.mLocalStorageReducer
    val mSerializer = Json {
        ignoreUnknownKeys = true
        isLenient = true
    }

    var mColorList: List<ColourDTO> by useState(listOf())
    var mColorListForIframe: List<ColourDTO>? by useState(listOf())
    val mIframeRef = useRef<HTMLIFrameElement>(null)

    var mFrameWidth by useState(0)
    var mFrameHeight by useState(0)

    val mNavigate = useNavigate()
    val mParams = useParams()
    var mIsLoading by useState(true)
    var mTargetScreen: String? by useState(null)
    val mToken: String? by useState(mParams["token"])
    val mType: EScreens.EColoursOnWallType by useState(kotlin.runCatching {
        EScreens.EColoursOnWallType.valueOf(
            mParams["type"] ?: ""
        )
    }.getOrElse { EScreens.EColoursOnWallType.TOKEN })
    var mIsColourApplied by useState(true)
    var mToastItem: ToastItem? by useState(null)

    useEffect(mIframeRef) {
        mIframeRef.current?.let {
            ResizeObserver {
                mFrameWidth = it.clientWidth
                mFrameHeight = it.clientHeight
            }.observe(it)
        }
    }

    val mIframeUrl = useMemo(mToken, mColorListForIframe, mFrameWidth, mFrameHeight) {
        URLBuilder(
            protocol = URLProtocol.HTTPS,
            host = common.EEnvironmentVariables.VEEUZE_HOST.getValue(),
            pathSegments = listOf("api", "showColourOnWall.php"),
            parameters = Parameters.build {
                append(
                    when (mType) {
                        EScreens.EColoursOnWallType.TOKEN -> "token"
                        EScreens.EColoursOnWallType.SCENE -> "sId"
                    }, mToken ?: ""
                )
                append("rectWidth", mFrameWidth.toString())
                append("rectHeight", mFrameHeight.toString())
                append("colours", mColorListForIframe?.joinToString(separator = ",") { it.mUnique } ?: "")
            }
        ).buildString()
    }
    useEffectOnce {

        val fixedList = mLocalStorage.mFixedList.mapIndexed { index, it ->
            ColourDTO(
                it.mUnique,
                index,
                mHighlight = false,
                mFixed = true,
                it.mName,
                it.mArtNr,
                it.mColourCode,
                it.mColourCollection,
                it.mAdditionalInfo1,
                it.mAdditionalInfo2,
                it.mEanCode
            )
        }

        val suggestedList = mLocalStorage.mSuggestedList.mapIndexed { index, it ->
            ColourDTO(
                it.mUnique,
                index,
                mHighlight = false,
                mFixed = false,
                it.mName,
                it.mArtNr,
                it.mColourCode,
                it.mColourCollection,
                it.mAdditionalInfo1,
                it.mAdditionalInfo2,
                it.mEanCode
            )
        }

        val list = fixedList.plus(suggestedList).also { colorList ->
            colorList.firstOrNull()?.let { it.mHighlight = true }
        }
        mColorList = list.toList()
        mColorListForIframe = list.toList()

        if (mType == EScreens.EColoursOnWallType.SCENE) {
            Piwik.trackContentInteraction(
                Piwik.CATEGORY_WALLCOLORER,
                Piwik.NAME_WALLCOLORER_USE_SCENE,
                mToken ?: "",
                Piwik.TARGET_SCENE
            )
        }

        val eventMethod: (Event) -> Unit = { event ->
            event as MessageEvent
            val target = event.data?.asJsObject().unsafeCast<String>()
            val dto = kotlin.runCatching { mSerializer.decodeFromString<ColourCollectionDTO>(target) }.getOrNull()

            when (dto?.mAction) {
                EPostMessageActions.SET_COLOURS -> {
                    mColorList = dto.mData ?: listOf()
                    mIsColourApplied = dto.mIsColourApplied ?: false
                }

                EPostMessageActions.READY -> {
                    mIsLoading = false
                }
                EPostMessageActions.SHARE_NATIVE -> {
                    val jsBridgeActionDTO = JSBridgeActionDTO(
                        EJSBridgeActions.SHARE_NATIVE,
                        dto.mBase64
                    )
                    window.postMessageToNativeApp(jsBridgeActionDTO)
                }
                EPostMessageActions.SHUFFLE -> {}
                EPostMessageActions.SET_HIGHLIGHT -> {}
                EPostMessageActions.EXPORT -> {}
                EPostMessageActions.TOGGLE_COLOUR -> {}
                EPostMessageActions.SET_FIXED_LIST -> {}
                EPostMessageActions.EXPORT_NATIVE -> {}
                null -> {}

            }.exhaustive
        }

        window.addEventListener(type = "message", eventMethod)

        cleanup {
            window.removeEventListener(type = "message", eventMethod)
        }
    }

    IonPage {
        id = EConstants.BURGER_MENU_CONTENT.mId

        IonHeader {
            Toolbar { mEndViews = listOf(WatchlistIcon, ProductlistIcon) }
        }

        IonContent {
            IonGrid {
                css(ECssClasses.VERTICAL_GRID.toClassName(), ECssClasses.NON_SCROLLABLE_GRID.toClassName()) {}
                IonRow {
                    css(ECssClasses.FULL_WIDTH_ROW.toClassName()) {
                        flexGrow = number(1.0)
                    }
                    IonCol {
                        css(ECssClasses.FULL_WIDTH_COL.toClassName()) { }
                        iframe {
                            css {
                                height = 100.pct
                                width = 100.pct
                                border = 0.px
                            }
                            id = "colourOnWallIframe"
                            ref = mIframeRef

                            src = mIframeUrl
                        }
                    }
                }

                IonRow {
                    IonCol {
                        button {
                            css(ECssClasses.ACTION_BUTTON.toClassName()) {
                                borderRadius = 50.pct
                                width = 48.px.plus(16.px)
                                height = 48.px.plus(16.px)
                                borderWidth = 8.px
                                borderColor = NamedColor.white
                                borderStyle = LineStyle.solid
                                left = 20.px
                                top = (-32).px
                                alignItems = AlignItems.center
                                position = Position.absolute
                                display = Display.flex
                                justifyContent = JustifyContent.spaceAround
                            }
                            onClick = {
                                mIframeRef.current?.contentWindow?.postMessage(
                                    Json.encodeToString(
                                        ColourCollectionDTO(
                                            EPostMessageActions.TOGGLE_COLOUR,
                                            null
                                        )
                                    ),
                                    "https://${EEnvironmentVariables.VEEUZE_HOST.getValue()}"
                                )
                            }
                            img {
                                css {
                                    height = 24.px
                                    width = 24.px
                                }
                                src =
                                    EImages.EYE_ICON(if (mIsColourApplied) EImages.EType.FILLED else EImages.EType.OUTLINED).mRelativePath
                            }
                        }

                        button {
                            css(ECssClasses.ACTION_BUTTON.toClassName()) {
                                borderRadius = 50.pct
                                width = 48.px.plus(16.px)
                                height = 48.px.plus(16.px)
                                borderWidth = 8.px
                                borderColor = NamedColor.white
                                borderStyle = LineStyle.solid
                                left = 104.px
                                top = (-32).px
                                alignItems = AlignItems.center
                                position = Position.absolute
                                display = Display.flex
                                justifyContent = JustifyContent.spaceAround
                            }
                            onClick = {
                                mColorList.find { it.mHighlight }?.let {
                                    mLocalStorageDispatch.invoke(
                                        LocalStorageDAO.ELocalStorageAction.ADD_OR_DELETE_FROM_PRODUCTLIST(it.toColourListObject())
                                    )
                                }
                                mToastItem = ToastItem(
                                    Localization.getLocalizationKey(
                                    if(mColorList.any{ it.mHighlight }) {
                                        if (mLocalStorage.mProductList.any {
                                                it.mUnique == (mColorList.find { it.mHighlight }?.mUnique ?: false)
                                            }) {
                                            ELocalizationKeys.CORE_TOAST_DELETE_FROM_PRODUCT_LIST
                                        } else ELocalizationKeys.CORE_TOAST_ADD_TO_PRODUCT_LIST
                                    }else {
                                        ELocalizationKeys.CORE_TOAST_NO_COLOUR_HIGHLIGHTED
                                    }
                                    )
                                )
                            }
                            img {
                                css {
                                    height = 24.px
                                    width = 24.px
                                }
                                src = EImages.SHOPPING_CART_ICON(
                                    EImages.EColor.WHITE,
                                    EImages.EType.fromIsOnList(mLocalStorage.mProductList.any {
                                        it.mUnique == (mColorList.find { it.mHighlight }?.mUnique ?: false)
                                    })
                                ).mRelativePath
                            }
                        }

                        if (window.isInNativeApp()) {
                            button {
                                css(ECssClasses.ACTION_BUTTON.toClassName()) {
                                    borderRadius = 50.pct
                                    width = 32.px.plus(16.px)
                                    height = 32.px.plus(16.px)
                                    borderWidth = 8.px
                                    borderColor = NamedColor.white
                                    borderStyle = LineStyle.solid
                                    right = 80.px
                                    top = (-24).px
                                    alignItems = AlignItems.center
                                    position = Position.absolute
                                    display = Display.flex
                                    justifyContent = JustifyContent.spaceAround
                                }
                                onClick = {
                                    mIframeRef.current?.contentWindow?.postMessage(
                                        Json.encodeToString(ColourCollectionDTO(EPostMessageActions.EXPORT_NATIVE)),
                                        "https://${EEnvironmentVariables.VEEUZE_HOST.getValue()}"
                                    )
                                    when (mType) {
                                        EScreens.EColoursOnWallType.TOKEN -> {
                                            Piwik.trackCustomEvent(
                                                Piwik.CATEGORY_WALLCOLORER,
                                                Piwik.NAME_WALLCOLORER_SHARE_IMAGE,
                                                Piwik.NAME_WALLCOLORER_SHARE_IMAGE
                                            )
                                        }
                                        EScreens.EColoursOnWallType.SCENE -> {
                                            Piwik.trackContentInteraction(
                                                Piwik.CATEGORY_WALLCOLORER,
                                                Piwik.NAME_WALLCOLORER_SHARE_SCENE,
                                                mToken ?: "",
                                                Piwik.TARGET_SCENE
                                            )
                                        }
                                    }
                                }
                                img {
                                    css {
                                        height = 24.px
                                        width = 24.px
                                        maxWidth = 24.px
                                    }
                                    src = EImages.SHARE_ICON(EImages.EColor.WHITE).mRelativePath
                                }
                            }
                        }

                        button {
                            css(ECssClasses.ACTION_BUTTON.toClassName()) {
                                borderRadius = 50.pct
                                width = 32.px.plus(16.px)
                                height = 32.px.plus(16.px)
                                borderWidth = 8.px
                                borderColor = NamedColor.white
                                borderStyle = LineStyle.solid
                                right = 20.px
                                top = (-24).px
                                alignItems = AlignItems.center
                                position = Position.absolute
                                display = Display.flex
                                justifyContent = JustifyContent.spaceAround
                            }
                            onClick = {
                                mIframeRef.current?.contentWindow?.postMessage(
                                    Json.encodeToString(
                                        ColourCollectionDTO(
                                            EPostMessageActions.EXPORT,
                                            null
                                        )
                                    ),
                                    "https://${EEnvironmentVariables.VEEUZE_HOST.getValue()}"
                                )
                                when (mType) {
                                    EScreens.EColoursOnWallType.TOKEN -> {
                                        Piwik.trackCustomEvent(
                                            Piwik.CATEGORY_WALLCOLORER,
                                            Piwik.NAME_WALLCOLORER_SAVE_IMAGE,
                                            Piwik.NAME_WALLCOLORER_SAVE_IMAGE
                                        )
                                    }
                                    EScreens.EColoursOnWallType.SCENE -> {
                                        Piwik.trackContentInteraction(
                                            Piwik.CATEGORY_WALLCOLORER,
                                            Piwik.NAME_WALLCOLORER_SAVE_SCENE,
                                            mToken ?: "",
                                            Piwik.TARGET_SCENE
                                        )
                                    }
                                }
                                if(!window.isInNativeApp()){
                                    mToastItem = ToastItem(
                                        Localization.getLocalizationKey(
                                            ELocalizationKeys.CORE_TOAST_SAVE_PICTURE_SUCCESSFULLY
                                        )
                                    )
                                }
                            }
                            img {
                                css {
                                    height = 24.px
                                    width = 24.px
                                    maxWidth = 24.px
                                }
                                src = EImages.DOWNLOAD_ICON(EImages.EColor.WHITE).mRelativePath
                            }
                        }
                    }

                    mToastItem?.let {
                        BottomToast {
                            this.mToastItem = it
                        }
                    }
                }

                IonRow {
                    IonCol {
                        size = "12"
                        css {
                            marginTop = 42.px
                        }
                        div {
                            css {
                                display = Display.flex
                                justifyContent =
                                    if (mColorList.size == 5) JustifyContent.spaceBetween else JustifyContent.spaceAround
                                padding = 20.px
                            }
                            mColorList.forEach { colour ->
                                ColourChip {
                                    mColourDTO = colour
                                    mOverrideFixedWith = false
                                    mOnClick = {
                                        val newList = mColorList.map {
                                            if (it == colour) it.copy(mHighlight = true) else it.copy(mHighlight = false)
                                        }
                                        mIframeRef.current?.contentWindow?.postMessage(
                                            Json.encodeToString(
                                                ColourCollectionDTO(
                                                    EPostMessageActions.SET_HIGHLIGHT,
                                                    newList
                                                )
                                            ),
                                            "https://${EEnvironmentVariables.VEEUZE_HOST.getValue()}"
                                        )
                                        mColorList = newList
                                        Piwik.trackContentInteraction(
                                            Piwik.CATEGORY_WALLCOLORER,
                                            Piwik.NAME_WALLCOLORER_USE_COLOR,
                                            colour.mUnique,
                                            Piwik.TARGET_COLOR
                                        )
                                    }
                                }
                            }
                        }
                    }
                }

                IonRow {
                    css(ECssClasses.BOTTOM_BUTTONS.toClassName()) {}
                    IonCol {
                        css {
                            justifyContent = JustifyContent.spaceBetween
                            display = Display.flex
                            padding = 0.px
                        }
                        size = "12"
                        div {
                            IonButton {
                                css(ECssClasses.SECONDARY_BUTTON.toClassName()) {
                                    maxWidth = 165.px
                                }
                                onClick = {
                                    mNavigate.invoke(-1)
                                }
                                ButtonContent {
                                    mContent =
                                        Localization.getLocalizationKey(ELocalizationKeys.CORE_BACK.name)
                                }
                            }
                        }
                        div {
                            IonButton {
                                css(ECssClasses.PRIMARY_BUTTON.toClassName()) {
                                    maxWidth = 165.px
                                    //marginBottom = 32.px
                                }
                                onClick = {
                                    mTargetScreen = EScreens.PRODUCT_LIST_SCREEN.mPath
                                }
                                ButtonContent {
                                    mContent =
                                        Localization.getLocalizationKey(ELocalizationKeys.S04_PRIMARY_NAVIGATION_BUTTON_CONTENT.name)
                                }
                            }
                        }
                    }
                }

                if (mIsLoading) {
                    LoadingSpinner()
                }
            }
        }

        mTargetScreen?.let {
            Navigate {
                to = it
            }
        }
    }
    BurgerMenu()
}